import { ArrowRight, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { ClassName } from "@/lib/rules/dnd5e/character/class-registry";
import type { WizardCharacterConfig } from "@/lib/rules/dnd5e/character/creation/current-wizard-config";
import type { Dnd5eAbilityScoreTag } from "@/lib/rules/dnd5e/vocabulary/ability-score";
import { type ClassOption, fetchClasses } from "@/src/api/classes";
import {
  ABILITIES,
  defaultPointBuyScores,
  isValidAssignment,
  isValidPointBuy,
  POINT_BUY_BUDGET,
  POINT_BUY_MAX,
  POINT_BUY_MIN,
  pointBuyCost,
  pointsSpent,
  setAbilityScore,
  swapAbilities,
} from "./ability-assignment";

export type { WizardCharacterConfig } from "@/lib/rules/dnd5e/character/creation/current-wizard-config";

type AbilityMethod = "standard-array" | "point-buy";

const ABILITY_LABELS: Record<Dnd5eAbilityScoreTag, string> = {
  str: "Strength",
  dex: "Dexterity",
  con: "Constitution",
  int: "Intelligence",
  wis: "Wisdom",
  cha: "Charisma",
};

function abilityModifier(score: number): string {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

type CharacterStepProps = {
  onNext: (config: Omit<WizardCharacterConfig, "species">) => void;
  onSkip?: () => void;
  onBack?: () => void;
};

export function CharacterStep({ onNext, onSkip, onBack }: CharacterStepProps) {
  const [name, setName] = useState("");
  const [classes, setClasses] = useState<ClassOption[] | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedClass, setSelectedClass] = useState<ClassOption | null>(null);
  const [method, setMethod] = useState<AbilityMethod>("standard-array");
  const [abilityScores, setAbilityScores] = useState<Record<
    Dnd5eAbilityScoreTag,
    number
  > | null>(null);
  // The ability currently "picked up" awaiting a swap partner.
  const [swapSource, setSwapSource] = useState<Dnd5eAbilityScoreTag | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    fetchClasses()
      .then((list) => {
        if (cancelled) {
          return;
        }
        setClasses(list);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setFetchError(
          err instanceof Error ? err.message : "Failed to load classes"
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectClass = (option: ClassOption) => {
    setSelectedClass(option);
    if (method === "standard-array") {
      setAbilityScores({ ...option.recommendedAbilities });
    }
    setSwapSource(null);
  };

  const switchMethod = (next: AbilityMethod) => {
    setMethod(next);
    if (next === "standard-array") {
      setAbilityScores(
        selectedClass ? { ...selectedClass.recommendedAbilities } : null
      );
    } else {
      setAbilityScores(defaultPointBuyScores());
    }
    setSwapSource(null);
  };

  const handleAbilityClick = (ability: Dnd5eAbilityScoreTag) => {
    if (abilityScores === null) {
      return;
    }
    if (swapSource === null) {
      setSwapSource(ability);
      return;
    }
    if (swapSource === ability) {
      setSwapSource(null);
      return;
    }
    setAbilityScores(swapAbilities(abilityScores, swapSource, ability));
    setSwapSource(null);
  };

  const adjustScore = (ability: Dnd5eAbilityScoreTag, delta: 1 | -1) => {
    if (abilityScores === null) {
      return;
    }
    setAbilityScores(
      setAbilityScore(abilityScores, ability, abilityScores[ability] + delta)
    );
  };

  const canIncrement = (ability: Dnd5eAbilityScoreTag): boolean => {
    if (abilityScores === null) {
      return false;
    }
    const score = abilityScores[ability];
    if (score >= POINT_BUY_MAX) {
      return false;
    }
    const addedCost = pointBuyCost(score + 1) - pointBuyCost(score);
    return pointsSpent(abilityScores) + addedCost <= POINT_BUY_BUDGET;
  };

  const spent = abilityScores !== null ? pointsSpent(abilityScores) : 0;

  const canCreate =
    name.trim().length > 0 &&
    selectedClass !== null &&
    abilityScores !== null &&
    (method === "standard-array"
      ? isValidAssignment(abilityScores)
      : isValidPointBuy(abilityScores));

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-8">
      <div className="text-center">
        <h2 className="font-semibold text-xl">Who are you?</h2>
        <p className="mt-1 text-muted-foreground text-sm">
          Name your hero and choose their path
        </p>
      </div>

      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-sm" htmlFor="character-name">
            Name
          </label>
          <Input
            autoComplete="off"
            id="character-name"
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mara Stormcloak"
            value={name}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-medium text-sm">Class</span>

          {classes === null && fetchError === null && (
            <div className="text-muted-foreground text-sm">
              Loading classes…
            </div>
          )}

          {fetchError !== null && (
            <div className="text-destructive text-sm">{fetchError}</div>
          )}

          {classes !== null && classes.length === 0 && (
            <div className="text-muted-foreground text-sm">
              No classes available.
            </div>
          )}

          {classes !== null && classes.length > 0 && (
            <div className="flex flex-col gap-3">
              {classes.map((option) => {
                const isSelected =
                  selectedClass?.className === option.className;
                return (
                  <Card
                    className={`cursor-pointer transition-colors ${
                      isSelected ? "border-primary" : "hover:border-primary/50"
                    }`}
                    key={option.className}
                    onClick={() => selectClass(option)}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">{option.label}</CardTitle>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {abilityScores !== null && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-sm">Ability scores</span>

              {/* Method toggle */}
              <div className="flex overflow-hidden rounded-md border border-input">
                <button
                  className={`flex-1 px-3 py-1.5 text-sm transition-colors ${
                    method === "standard-array"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => switchMethod("standard-array")}
                  type="button"
                >
                  Standard Array
                </button>
                <button
                  className={`flex-1 border-input border-l px-3 py-1.5 text-sm transition-colors ${
                    method === "point-buy"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => switchMethod("point-buy")}
                  type="button"
                >
                  Point Buy
                </button>
              </div>
            </div>

            {method === "standard-array" && (
              <div className="flex flex-col gap-2">
                <p className="text-muted-foreground text-xs">
                  Tap one ability, then another, to swap their scores.
                </p>
                <div className="flex flex-col gap-2">
                  {ABILITIES.map((ability) => {
                    const isPicked = swapSource === ability;
                    return (
                      <button
                        className={`flex items-center justify-between rounded-md border px-3 py-2 text-left transition-colors ${
                          isPicked
                            ? "border-primary bg-primary/10"
                            : "border-input hover:border-primary/50"
                        }`}
                        key={ability}
                        onClick={() => handleAbilityClick(ability)}
                        type="button"
                      >
                        <span className="text-sm">
                          {ABILITY_LABELS[ability]}
                        </span>
                        <span className="flex items-baseline gap-1.5 tabular-nums">
                          <span className="font-semibold text-base">
                            {abilityScores[ability]}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            ({abilityModifier(abilityScores[ability])})
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {method === "point-buy" && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Points spent</span>
                  <span className="font-medium tabular-nums">
                    {spent} / {POINT_BUY_BUDGET}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {ABILITIES.map((ability) => (
                    <div
                      className="flex items-center justify-between rounded-md border border-input px-3 py-2"
                      key={ability}
                    >
                      <span className="text-sm">{ABILITY_LABELS[ability]}</span>
                      <div className="flex items-center gap-2">
                        <button
                          aria-label={`Decrease ${ABILITY_LABELS[ability]}`}
                          className="flex h-7 w-7 items-center justify-center rounded border border-input transition-colors hover:bg-muted disabled:opacity-30"
                          disabled={abilityScores[ability] <= POINT_BUY_MIN}
                          onClick={() => adjustScore(ability, -1)}
                          type="button"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="flex w-16 items-baseline justify-center gap-1 tabular-nums">
                          <span className="font-semibold text-base">
                            {abilityScores[ability]}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            ({abilityModifier(abilityScores[ability])})
                          </span>
                        </span>
                        <button
                          aria-label={`Increase ${ABILITY_LABELS[ability]}`}
                          className="flex h-7 w-7 items-center justify-center rounded border border-input transition-colors hover:bg-muted disabled:opacity-30"
                          disabled={!canIncrement(ability)}
                          onClick={() => adjustScore(ability, 1)}
                          type="button"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <Button
          className="w-48"
          disabled={!canCreate}
          onClick={() => {
            if (canCreate && selectedClass !== null && abilityScores !== null) {
              onNext({
                name: name.trim(),
                class: selectedClass.className as ClassName,
                abilityScores,
                classProfileId: selectedClass.classProfileId,
              });
            }
          }}
          type="button"
        >
          Create
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              className="text-muted-foreground text-sm underline transition-colors hover:text-foreground"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
          )}
          {onSkip ? (
            <button
              className="text-muted-foreground text-sm underline transition-colors hover:text-foreground"
              onClick={onSkip}
              type="button"
            >
              Skip character
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
